import { motion } from "framer-motion";
import { FiUsers, FiActivity, FiHeart } from "react-icons/fi";
import { BiMusic, BiFootball, BiPaint, BiCode, BiCamera, BiBook } from "react-icons/bi";
import { useSiteSettings } from "../context/SiteSettingsContext";

const clubs = [
  { icon: BiMusic,  name: "Music & Choir Club",      desc: "Vocal and instrumental music training, annual concerts and school events." },
  { icon: BiPaint,  name: "Arts & Crafts Club",       desc: "Creative expression through painting, sculpture, and mixed media art." },
  { icon: BiCode,   name: "Coding & Robotics Club",   desc: "Programming, robotics, and technology innovation projects." },
  { icon: BiCamera, name: "Photography Club",         desc: "Photography skills, darkroom techniques, and creative storytelling." },
  { icon: BiBook,   name: "Debate & Literature Club", desc: "Public speaking, debates, and literary appreciation activities." },
  { icon: FiUsers,  name: "Eco & Environment Club",   desc: "Promoting environmental awareness and sustainable school practices." },
];

const sports = [
  { name: "Cricket",      icon: "🏏", level: "Inter-school competitions" },
  { name: "Football",     icon: "⚽", level: "District level tournaments" },
  { name: "Basketball",   icon: "🏀", level: "School league" },
  { name: "Volleyball",   icon: "🏐", level: "Annual tournaments" },
  { name: "Table Tennis", icon: "🏓", level: "School competitions" },
  { name: "Athletics",    icon: "🏃", level: "Track & field events" },
  { name: "Badminton",    icon: "🏸", level: "School league" },
  { name: "Chess",        icon: "♟️", level: "Inter-school meets" },
];

const community = [
  { title: "Annual Charity Drive",   desc: "Students organise fundraising events to support underprivileged communities in Nepal." },
  { title: "Environmental Clean-up", desc: "Monthly clean-up drives in and around school to promote environmental responsibility." },
  { title: "Blood Donation Camp",    desc: "Annual blood donation camp organised in collaboration with Red Cross Society." },
  { title: "Old Age Home Visit",     desc: "Regular visits to local old age homes to spread joy and provide companionship." },
];

const facilities = [
  { title: "Smart Library",        desc: "10,000+ books, digital resources, e-library access, and dedicated reading rooms.",           icon: BiBook },
  { title: "Sports Complex",       desc: "Multi-purpose indoor sports hall, cricket ground, and athletics track.",                     icon: BiFootball },
  { title: "Science Labs",         desc: "Three fully-equipped labs — Physics, Chemistry, and Biology with modern instruments.",        icon: FiActivity },
  { title: "Performing Arts Hall", desc: "Fully-equipped auditorium for drama, music performances, and school events.",                icon: BiMusic },
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

export default function StudentLife() {
  const { settings } = useSiteSettings();

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Beyond Academics</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Student <span className="text-sky-300">Life</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              At {settings?.schoolName || "Hillbert English Boarding School "}, education goes beyond textbooks.
              Discover the vibrant student experience.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Clubs */}
      <section id="clubs" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Explore Your Passion" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Clubs &amp; <span className="text-[#2563EB]">Societies</span></h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clubs.map((club, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-blue-100 shadow-sm hover:shadow-lg hover:border-[#2563EB]/30 transition-all duration-300 group"
              >
                <div className="w-11 h-11 bg-[#2563EB] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#1D4ED8] transition-colors shadow-sm">
                  <club.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-[#0F172A] group-hover:text-[#2563EB] transition-colors mb-2">{club.name}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{club.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports */}
      <section id="sports" className="py-20 lg:py-28 bg-[#F0F4FF]">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Physical Excellence" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Sports <span className="text-[#2563EB]">Programs</span></h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {sports.map((sport, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: idx * 0.07 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 text-center border border-blue-100 shadow-sm hover:shadow-lg hover:border-[#2563EB]/30 transition-all duration-300 group"
              >
                <div className="text-3xl mb-3">{sport.icon}</div>
                <h4 className="font-bold text-[#0F172A] group-hover:text-[#2563EB] text-sm mb-1 transition-colors">{sport.name}</h4>
                <p className="text-slate-400 text-xs">{sport.level}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community */}
      <section id="community" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Giving Back" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Community <span className="text-[#2563EB]">Activities</span></h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {community.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#F0F4FF] rounded-2xl p-8 flex gap-5 border border-blue-100 hover:border-[#2563EB]/30 hover:shadow-md transition-all group"
              >
                <div className="w-11 h-11 bg-[#2563EB] rounded-xl flex items-center justify-center shrink-0 shadow-sm group-hover:bg-[#1D4ED8] transition-colors">
                  <FiHeart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section id="facilities" className="py-20 lg:py-28 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <span className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Our Infrastructure</span>
            </div>
            <h2 className="text-4xl font-bold text-white">Key <span className="text-sky-300">Facilities</span></h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {facilities.map((facility, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 rounded-2xl p-8 flex gap-5 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#2563EB] transition-colors">
                  <facility.icon className="w-6 h-6 text-sky-300 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg mb-2">{facility.title}</h3>
                  <p className="text-blue-200/70 text-sm leading-relaxed">{facility.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
